/**
 * shared/attachmentsPanel/Uploader.jsx - the uploader box inside a folder
 * (a drop zone, the picked file, its label fields). A render function called
 * inline, so it renders the same DOM the panel used to build itself.
 */
import { REF_OPTIONAL } from '../../../utils/attachmentPreview';
import { acceptDrag } from '../../../utils/droppedFiles';
import { GREEN, humanSize, FIELD } from './panelParts';

export function renderUploader({
  setDropTarget,
  view,
  handleDrop,
  dropTarget,
  bulk,
  inputRef,
  pickFile,
  picked,
  suggested,
  docType,
  setDocType,
  setError,
  docRef,
  setDocRef,
  error,
  clearPicker,
  upload,
  ready,
  uploading,
}) {
  return (
    <>
      {/* Uploader — the whole box is a drop zone, not just the Browse button. */}
      <div
        onDragOver={(e) => {
          acceptDrag(e);
          setDropTarget(view.key);
        }}
        onDragLeave={() => setDropTarget(null)}
        onDrop={(e) => handleDrop(e, view.key, view.subId)}
        style={{
          marginBottom: 14,
          borderRadius: 8,
          padding: 10,
          border: `1.5px dashed ${dropTarget === view.key ? GREEN : '#D7E3D3'}`,
          background: dropTarget === view.key ? '#EDF7EC' : 'transparent',
          transition: 'background 120ms',
        }}
      >
        <div style={{ fontSize: 10, color: dropTarget === view.key ? GREEN : '#9AA396', fontWeight: 700, marginBottom: 7 }}>
          {dropTarget === view.key
            ? `Drop to add to ${view.label}`
            : `Drag files here (from your folders, Outlook or Teams), paste with Ctrl+V, or pick one:`}
        </div>
        {bulk && (
          <div style={{ fontSize: 10.5, fontWeight: 700, color: GREEN, marginBottom: 7 }}>
            {bulk.note || `Uploading ${bulk.done} of ${bulk.total}…`}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          onChange={(e) => pickFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          style={{ fontSize: 10.5, fontFamily: 'Montserrat', width: '100%', color: '#555' }}
        />

        {/* Label the file — appears once one is picked so the panel stays quiet otherwise. */}
        {picked && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: '#F6F9F3', border: '1px solid #DCE7D6', borderRadius: 8 }}>
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 800,
                color: '#557052',
                letterSpacing: 1,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              Label this file
            </div>
            {/* Name it here too: this panel is the one place BOTH the drop and
                the Browse path are guaranteed to render. */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', marginBottom: 8, wordBreak: 'break-all' }}>
              {picked.name} <span style={{ fontWeight: 400, color: '#8A948A' }}>{humanSize(picked.size)}</span>
            </div>
            {suggested && (
              <div
                style={{
                  marginBottom: 8,
                  fontSize: 10.5,
                  color: '#33691E',
                  background: '#F1F8E9',
                  border: '1px solid #DCEDC8',
                  borderRadius: 6,
                  padding: '5px 10px',
                }}
              >
                <b>Suggested:</b> {suggested.type === 'po' ? 'PO' : suggested.type[0].toUpperCase() + suggested.type.slice(1)}{' '}
                {suggested.ref}
                <span style={{ color: '#7a8f74' }}> ({suggested.why} - change below if wrong)</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                value={docType}
                onChange={(e) => {
                  setDocType(e.target.value);
                  setError('');
                }}
                style={{ ...FIELD, flex: '1 1 120px', fontWeight: 700, color: docType ? '#1a1a1a' : '#999' }}
              >
                <option value="">Type…</option>
                <option value="invoice">Invoice</option>
                <option value="po">PO</option>
                <option value="quote">Quote</option>
                <option value="rfq">RFQ</option>
                <option value="general">General / other</option>
              </select>
              <input
                type="text"
                value={docRef}
                onChange={(e) => {
                  setDocRef(e.target.value);
                  setError('');
                }}
                placeholder={REF_OPTIONAL.has(docType) ? 'Number (optional)' : 'Number *'}
                style={{ ...FIELD, flex: '1 1 130px' }}
              />
            </div>
            {error && <div style={{ marginTop: 7, fontSize: 10.5, fontWeight: 700, color: '#B71C1C' }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button
                type="button"
                onClick={clearPicker}
                style={{
                  background: '#fff',
                  color: '#B71C1C',
                  border: '1.5px solid #EF9A9A',
                  padding: '7px 18px',
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={upload}
                disabled={!ready || uploading}
                style={{
                  background: !ready || uploading ? '#A5C4A7' : GREEN,
                  color: '#fff',
                  border: 'none',
                  padding: '7px 18px',
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: !ready || uploading ? 'default' : 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
